import { useEffect, useState } from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import type { ICustomAuthPublicClientApplication } from '@azure/msal-browser/custom-auth';

import { ReportForm } from './ReportForm';
import { SignInPanel } from './SignInPanel';
import { endSession, getSession, saveDefaultStore, type SessionInfo } from './session';
import { signOut } from './nativeAuth';

interface AppProps {
    client: ICustomAuthPublicClientApplication;
    allowedDomains: string[];
    allowedAddresses: string[];
}

/**
 * "@lowes.com or @store.lowes.com" - built from config so the copy can never
 * drift from what the app actually accepts.
 */
function domainsSentence(domains: string[]): string {
    const list = domains.map((d) => `@${d}`);
    if (list.length <= 1) return list[0] ?? '';
    return `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}`;
}

function Masthead() {
    return (
        <header className="masthead">
            <h1>Lowe&rsquo;s Store Counts</h1>
            <p>Consignment based product</p>
        </header>
    );
}

/**
 * The session cookie is the source of truth, not the MSAL account.
 *
 * MSAL's own tokens expire after 24 hours and cannot be extended, so trusting
 * them here would sign an associate out daily no matter what the server says.
 * The server validated the identity once and issued a 90-day cookie; asking it
 * "am I signed in?" is the only question worth asking on load.
 *
 * The domain allowlist is enforced server-side before the cookie is ever
 * issued, so a session that exists is a session that is allowed. The client
 * check that used to live here was a convenience, not a control.
 */
export default function App({ client, allowedDomains, allowedAddresses }: AppProps) {
    const [session, setSession] = useState<SessionInfo | null | undefined>(undefined);
    const domains = domainsSentence(allowedDomains);

    useEffect(() => {
        void getSession().then(setSession);
    }, []);

    const onSignedIn = (_account: AccountInfo | null) => {
        // The cookie was just minted by SignInPanel; re-read it rather than
        // reconstructing the session from the MSAL account.
        void getSession().then(setSession);
    };

    const leave = async () => {
        await signOut(client);
        await endSession();
        setSession(null);
    };

    if (session === undefined) {
        return (
            <div className="app">
                <Masthead />
                <div className="content">
                    <p className="empty">Loading...</p>
                </div>
            </div>
        );
    }

    if (session === null) {
        return (
            <div className="app">
                <Masthead />
                <SignInPanel
                    client={client}
                    allowedDomains={allowedDomains}
                    allowedAddresses={allowedAddresses}
                    domainsSentence={domains}
                    onSignedIn={onSignedIn}
                />
            </div>
        );
    }

    return (
        <div className="app">
            <Masthead />
            {/*
             * Always visible: a session lasts months and a store terminal may be
             * shared, so whoever is about to submit can see whose name goes on
             * the report without going looking for it.
             */}
            <div className="who">
                <span className="who-email" title={session.email}>
                    Reporting as {session.email}
                </span>
                <button type="button" className="who-switch" onClick={() => void leave()}>
                    Log out
                </button>
            </div>
            <ReportForm
                defaultStore={session.defaultStore}
                onStoreChange={(id) => void saveDefaultStore(id)}
            />
        </div>
    );
}
